import React from 'react';
import moment from 'moment';
import { config } from 'config/config';
import Switch from 'components/Form/Switch';
import generalHelpers from 'utils/generalHelpers';

type FieldType = 'SWITCH' | 'DATE' | 'MULTIPLE_CHOICE' | 'HYPERLINK' | string;

const EMPTY_CELL = '-';

export interface CustomField {
    title: string;
    slug?: string;
    uuid?: string;
    field_type?: FieldType;
    display?: string | '0' | '1';
}

interface BuildParams<TRecord = any> {
    learningProgramCustomColumns?: boolean;
    customFields?: CustomField[] | null;
    data?: TRecord[] | null;
    baseColumns: any[];
    hasCustomSorter?: boolean;
}

export function buildCustomFieldsColumns<TRecord = any>({
    learningProgramCustomColumns,
    customFields,
    data,
    baseColumns,
    hasCustomSorter = false,
}: BuildParams<TRecord>): any[] {
    if (
        !learningProgramCustomColumns ||
        !Array.isArray(customFields) ||
        customFields.length === 0 ||
        !Array.isArray(data) ||
        data.length === 0
    ) {
        return baseColumns;
    }

    const visibleFields = customFields.filter(f => f.display, true);
    if (visibleFields.length === 0) {
        return baseColumns;
    }

    const updatedColumns = [...baseColumns];
    let actionsCol: any | null = null;
    const actionsIdx = updatedColumns.findIndex(c => c?.key === 'actions');
    if (actionsIdx >= 0) {
        actionsCol = updatedColumns[actionsIdx];
        updatedColumns.splice(actionsIdx, 1);
    }

    const normalize = (s: unknown) =>
        String(s ?? '')
            .normalize('NFKD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .toLowerCase();

    const camel = (s: string) => normalize(generalHelpers.camelize(s));

    const findFirstExistingKey = (aliases: string[]) => {
        for (const row of data) {
            const rowKeys = Object.keys(row ?? {});
            const match = rowKeys.find(k => aliases.includes(normalize(k)));
            if (match) {
                return match;
            }
        }

        return null;
    };

    const getByAliases = (record: any, aliases: string[]) => {
        for (const key of Object.keys(record ?? {})) {
            if (aliases.includes(normalize(key))) {
                return record[key];
            }
        }

        return undefined;
    };

    visibleFields.forEach(field => {
        const aliases = Array.from(
            new Set([
                    normalize(field.title),
                    camel(field.title),
                    normalize(field.slug),
                    normalize(field.uuid),
                ].filter(Boolean) as string[]
            )
        );

        const matchKey = findFirstExistingKey(aliases);
        const stableKey = field.uuid ?? matchKey ?? field.slug ?? field.title;

        const column: any = {
            title: field.title,
            sorter: hasCustomSorter,
            key: stableKey,
            render: (_: unknown, record: any) => {
                const raw = getByAliases(record, aliases);
                return raw ?? EMPTY_CELL;
            },
        };

        switch (field.field_type) {
            case 'SWITCH':
                column.render = (_: unknown, record: any) => {
                    const raw = getByAliases(record, aliases);
                    return React.createElement(Switch as any, {
                        disabled: true,
                        defaultChecked: !!raw,
                    });
                };
                break;

            case 'DATE':
                column.render = (_: unknown, record: any) => {
                    const raw = getByAliases(record, aliases);
                    return raw ? moment(raw).format(config.defaultDateFormat) : EMPTY_CELL;
                };
                break;

            case 'MULTIPLE_CHOICE':
                column.render = (_: unknown, record: any) => {
                    let raw = getByAliases(record, aliases);
                    if (raw == null || raw === '') {
                        return EMPTY_CELL;
                    }

                    let arr: any = raw;
                    if (typeof raw === 'string') {
                        try {
                            arr = JSON.parse(raw);
                        } catch {
                            if (raw.includes(',')) {
                                arr = raw.split(',').map(s => s.trim()).filter(Boolean);
                            }
                        }
                    }

                    if (!Array.isArray(arr) || arr.length === 0) {
                        return EMPTY_CELL;
                    }

                    return arr.map((el:string) => el + '; ');
                };
                break;

            case 'HYPERLINK':
                column.render = (_: unknown, record: any) => {
                    const raw = getByAliases(record, aliases);
                    if (!raw) {
                        return EMPTY_CELL;
                    }

                    try {
                        const params = typeof raw === 'string' ? JSON.parse(raw) : raw;
                        if (!params?.hyperlink || !params?.displayName) {
                            return EMPTY_CELL;
                        }

                        return React.createElement(
                            'div',
                            {
                                className: 'hyperlink-tag pointer',
                                onClick: () => window.open(params.hyperlink, '_blank'),
                            },
                            params.displayName
                        );
                    } catch {
                        return EMPTY_CELL;
                    }
                };
                break;

            default:
                break;
        }

        updatedColumns.push(column);
    });

    if (actionsCol) {
        updatedColumns.push(actionsCol);
    }

    return updatedColumns;
}
